import React, { useState, useRef, useEffect } from 'react'
import { Camera, Square, Play, Image, Video, Sparkles } from 'lucide-react'

interface CameraInterfaceProps {
  isActive: boolean
  onCameraToggle: (active: boolean) => void
}

const CameraInterface: React.FC<CameraInterfaceProps> = ({ isActive, onCameraToggle }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<string | null>(null)

  useEffect(() => {
    if (isActive && !stream) {
      startCamera()
    } else if (!isActive && stream) {
      stopCamera()
    }

    return () => {
      if (stream) {
        stopCamera()
      }
    }
  }, [isActive])

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'environment'
        }
      })
      
      setStream(mediaStream)
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
      }
    } catch (error) {
      console.error('Error accessing camera:', error)
      onCameraToggle(false)
    }
  }

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    setCapturedImage(null)
    setAnalysisResult(null)
  }

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current
      const canvas = canvasRef.current
      const context = canvas.getContext('2d')
      
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      context?.drawImage(video, 0, 0, canvas.width, canvas.height)
      
      const imageData = canvas.toDataURL('image/jpeg')
      setCapturedImage(imageData)
    }
  }

  const analyzeImage = async () => {
    if (!capturedImage) return
    
    setIsProcessing(true)
    setAnalysisResult(null)
    
    try {
      // Extract base64 data from data URL
      const base64Image = capturedImage.split(',')[1]
      
      // Send to enhanced backend for analysis
      const response = await fetch('/api/camera/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_data: base64Image,
          session_id: 'camera_client_' + Date.now(),
          prompt: 'What do you see in this image? Please provide a detailed analysis.'
        }),
      })
      
      if (response.ok) {
        const result = await response.json()
        setAnalysisResult(result.description)
      } else {
        setAnalysisResult('Analysis failed. Please try again.')
      }
    } catch (error) {
      console.error('Error analyzing image:', error)
      setAnalysisResult('Error analyzing image. Please check your connection.')
    } finally {
      setIsProcessing(false)
    }
  }

  const toggleCamera = () => {
    onCameraToggle(!isActive)
  }

  return (
    <div className="h-full flex flex-col p-6 space-y-6 bg-gradient-to-br from-blue-900/20 to-purple-900/20">
      {/* Camera Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            📷 Camera Vision
          </h2>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            isActive ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'
          }`}>
            {isActive ? '🟢 Live' : '🔴 Offline'}
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={toggleCamera}
            className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all transform hover:scale-105 ${
              isActive
                ? 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white shadow-lg'
                : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg'
            }`}
          >
            {isActive ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span>{isActive ? 'Stop' : 'Start'} Camera</span>
          </button>
          
          {isActive && (
            <button
              onClick={captureImage}
              disabled={!isActive}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-medium transition-all transform hover:scale-105 shadow-lg disabled:opacity-50"
            >
              <Camera className="w-4 h-4" />
              <span>Capture</span>
            </button>
          )}
        </div>
      </div>

      {/* Camera Feed and Analysis Area */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Camera Feed */}
        <div className="bg-black/20 backdrop-blur-md rounded-2xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-purple-300 mb-4 flex items-center">
            <Video className="w-5 h-5 mr-2" />
            Live Camera Feed
          </h3>
          
          {isActive ? (
            <div className="relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-64 lg:h-80 object-cover rounded-xl camera-feed border border-white/20"
              />
              <canvas ref={canvasRef} className="hidden" />
              
              {/* Overlay grid for better framing */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="grid grid-cols-3 grid-rows-3 h-full">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="border border-white/10 border-dashed" />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full h-64 lg:h-80 bg-black/30 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/10">
              <div className="text-center text-purple-400">
                <Camera className="w-16 h-16 mx-auto mb-4 opacity-50 animate-pulse" />
                <p className="text-lg">Camera is offline</p>
                <p className="text-sm text-purple-500">Click "Start Camera" to begin</p>
              </div>
            </div>
          )}
        </div>

        {/* Analysis Panel */}
        <div className="bg-black/20 backdrop-blur-md rounded-2xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-purple-300 mb-4 flex items-center">
            <Sparkles className="w-5 h-5 mr-2" />
            AI Image Analysis
          </h3>
          
          {capturedImage ? (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="w-full h-48 object-cover rounded-xl border border-white/10"
                />
                <button
                  onClick={() => setCapturedImage(null)}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-all"
                >
                  <Square className="w-4 h-4" />
                </button>
              </div>
              
              <button
                onClick={analyzeImage}
                disabled={isProcessing}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-medium transition-all transform hover:scale-105 shadow-lg disabled:opacity-50"
              >
                <Image className="w-4 h-4" />
                <span>{isProcessing ? '🔍 Analyzing...' : '✨ Analyze Image'}</span>
              </button>
              
              {analysisResult && (
                <div className="bg-black/30 backdrop-blur-md rounded-xl p-4 border border-purple-500/30">
                  <h4 className="font-semibold text-purple-400 mb-2 flex items-center">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analysis Result:
                  </h4>
                  <p className="text-purple-200 leading-relaxed">{analysisResult}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center bg-black/30 backdrop-blur-md rounded-xl border border-white/10">
              <div className="text-center text-purple-400">
                <Image className="w-16 h-16 mx-auto mb-4 opacity-50 animate-pulse" />
                <p className="text-lg">No image captured</p>
                <p className="text-sm text-purple-500">Capture an image to analyze</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Real-time Analysis Controls */}
      <div className="bg-black/20 backdrop-blur-md rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-purple-300 mb-4 flex items-center">
          <Sparkles className="w-5 h-5 mr-2" />
          Real-time Analysis Features
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center space-x-3 p-4 bg-black/30 backdrop-blur-md hover:bg-purple-600/20 rounded-xl transition-all border border-white/10 hover:border-purple-400/50">
            <Video className="w-6 h-6 text-blue-400" />
            <div>
              <span className="text-purple-200 font-medium">Object Detection</span>
              <p className="text-xs text-purple-400">Identify objects in real-time</p>
            </div>
          </button>
          
          <button className="flex items-center space-x-3 p-4 bg-black/30 backdrop-blur-md hover:bg-purple-600/20 rounded-xl transition-all border border-white/10 hover:border-purple-400/50">
            <Image className="w-6 h-6 text-green-400" />
            <div>
              <span className="text-purple-200 font-medium">Text Recognition</span>
              <p className="text-xs text-purple-400">Extract text from images</p>
            </div>
          </button>
          
          <button className="flex items-center space-x-3 p-4 bg-black/30 backdrop-blur-md hover:bg-purple-600/20 rounded-xl transition-all border border-white/10 hover:border-purple-400/50">
            <Camera className="w-6 h-6 text-purple-400" />
            <div>
              <span className="text-purple-200 font-medium">Scene Analysis</span>
              <p className="text-xs text-purple-400">Understand visual context</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

export default CameraInterface